import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Toast } from 'primereact/toast';

interface ToastContextType {
  showToast: (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string,
    life?: number
  ) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toast = useRef<Toast>(null);

  const showToast = useCallback(
    (
      severity: 'success' | 'info' | 'warn' | 'error',
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  // Wrap the value in useMemo to ensure stable reference
  const contextValue = useMemo(
    () => ({
      showToast,
    }),
    [showToast] // Dependencies are the memoized values or functions
  );

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast ref={toast} />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
